import React, { useMemo, useState } from 'react'
import type { QuoteFile } from 'paintscout'
import type { QuoteItemSection } from '@paintscout/util/builder'
import { getItems, getFilesForItem, trimGroup } from '@paintscout/util/builder'
import { Grid } from '@paintscout/ui'
import type { PresentationBlockProps } from '../../'
import { useQuote, usePresentation } from '../../../context'
import { PresentationFilesGrid, ViewMediaDialog } from '../../components'
import { useClientOptions, useDialogs } from '@paintscout/ui-business'
import PicturesPlaceholder from './PicturesPlaceholder'

const SECTIONS: QuoteItemSection[] = ['bid', 'additional', 'added-options', 'pending', 'options']
const CONSUMER = 'customer'
// Same pagination boundary FilesByItem uses for the v1/work-order media grid (#14984) - this
// flat gallery had no "Load More" at all until this fix, so a large quote rendered every file's
// image at once.
const FILES_PER_PAGE = 30

export function isVisibleToConsumer(file: QuoteFile) {
  if (file.visibility === 'visible') return true
  if (file.visibility === 'hidden') return false
  return file.visibility === CONSUMER
}

/**
 * A live gallery of the quote's own job pictures — no configurable fields, entirely derived from
 * the quote in context. Rendered flat (not grouped per line item like the v1/FilesByItem view) so
 * it shares Gallery's own grid + lightbox (PresentationFilesGrid/ViewMediaDialog) instead of the
 * legacy FilesGrid/FilesLightboxDialog pair FilesByItem uses.
 */
export default function PicturesBlock(props: PresentationBlockProps) {
  const { WrapperComponent, isSettings, presentationIndustry } = props
  const { openDialog, dismissDialog } = useDialogs()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const { presentationLibrary } = usePresentation()
  const [, setIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(FILES_PER_PAGE)
  // PDF generation drives headless Chrome, which never clicks "Load More" - it only screenshots
  // whatever the initial render produced. Paginating there would silently drop files past
  // FILES_PER_PAGE from the PDF instead of just being slow (see docs/flows/pdf-generation.md).
  const isPdfRender = useMemo(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pdf') === '1',
    []
  )

  const files = useMemo(() => {
    const view = 'area'
    const groups = Object.values(quote.groups ?? {})
    const allItems = SECTIONS.reduce(
      (items, section) => [
        ...items,
        ...getItems({
          quote,
          consumer: CONSUMER,
          section,
          view,
          options,
          overrideOptions: { showHiddenItems: true, includeAreasWithNoSubstrates: true }
        })
      ],
      []
    ).filter((item) => !groups.some((group) => group?.children?.includes(item.key)))

    const trimmedGroups = groups
      .filter((group) => SECTIONS.includes(group.section))
      .map((group) => trimGroup(group, options, quote))

    return [...allItems, ...trimmedGroups].flatMap((item) =>
      getFilesForItem({ quote, options, itemKey: item.key }).filter(isVisibleToConsumer)
    )
  }, [quote, options])

  if (!files.length && !isSettings) {
    return null
  }

  // Always show everything for a PDF render, which never clicks "Load More" - see isPdfRender.
  const skipPagination = isPdfRender
  const visibleFiles = skipPagination ? files : files.slice(0, visibleCount)
  const hasMore = !skipPagination && visibleCount < files.length

  function handleViewFiles(fileIndex: number) {
    // The lightbox opens over the full set, not just the visible page, so paging through it
    // doesn't dead-end at whatever's currently loaded in the grid.
    openDialog(ViewMediaDialog, {
      files: files.filter((file) => !!file?.src),
      index: fileIndex,
      onIndexChange: setIndex,
      enableDownload: !isSettings,
      onClose: dismissDialog
    })
  }

  return (
    <WrapperComponent {...props}>
      {isSettings ? (
        <PicturesPlaceholder />
      ) : (
        <Grid container spacing={3} justifyContent={'center'} alignItems={'flex-start'}>
          <Grid item xs={12}>
            <PresentationFilesGrid
              isEditable={false}
              isSettings={isSettings}
              inPresentation={true}
              justify="space-around"
              files={visibleFiles}
              presentationIndustry={presentationIndustry}
              presentationLibrary={presentationLibrary}
              showDetails={false}
              onFileClick={handleViewFiles}
              fullItemHeight={true}
              filePreviewProps={{ showFirstPageOnly: true }}
              // Synchronous local state update - `async` only exists to satisfy
              // PresentationFilesGrid's Load More button, which awaits this to drive its spinner.
              onLoadMore={
                hasMore
                  ? async () => {
                      setVisibleCount((count) => count + FILES_PER_PAGE)
                    }
                  : undefined
              }
            />
          </Grid>
        </Grid>
      )}
    </WrapperComponent>
  )
}
